import { useEffect, useState, type HTMLAttributes, type ReactElement, type ReactNode } from "react";
import { cn } from "@/shared/utils/cn";

export interface ResponsiveSidebarProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean;
  sidebarId?: string;
  sidebarLabel?: string;
  sidebar: ReactNode;
  children: ReactNode;
  onBackdropClick?: () => void;
}

function isDesktopViewport(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia("(min-width: 1024px)").matches;
}

export function ResponsiveSidebar({
  open,
  sidebarId,
  sidebarLabel,
  sidebar,
  children,
  className,
  onBackdropClick,
  ...props
}: ResponsiveSidebarProps): ReactElement {
  const [isDesktop, setIsDesktop] = useState(isDesktopViewport);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const syncDesktopState = (): void => setIsDesktop(mediaQuery.matches);

    syncDesktopState();
    mediaQuery.addEventListener("change", syncDesktopState);

    return () => {
      mediaQuery.removeEventListener("change", syncDesktopState);
    };
  }, []);

  const isMobileHidden = !isDesktop && !open;

  return (
    <div className={cn("min-h-screen lg:grid lg:grid-cols-[18rem_1fr]", className)} {...props}>
      {!isDesktop && open ? (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onBackdropClick}
        />
      ) : null}
      <aside
        id={sidebarId}
        aria-label={sidebarLabel}
        role={!isDesktop ? "dialog" : undefined}
        aria-modal={!isDesktop ? true : undefined}
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-72 border-r bg-background p-4 transition-transform lg:static lg:z-auto lg:w-auto lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
        aria-hidden={isMobileHidden ? true : undefined}
        inert={isMobileHidden || undefined}
      >
        {sidebar}
      </aside>
      <main className="min-w-0">{children}</main>
    </div>
  );
}
